import { useCallback, useEffect, useState } from 'react'

import { Box, FileText, Plus, Wrench } from '@gravity-ui/icons'
import { Button, Tabs } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useWarehouseStore } from '@/store/warehouse.store'

import { WarehouseImportDialog } from './components/WarehouseImportDialog'
import { WarehouseInstallDialog } from './components/WarehouseInstallDialog'
import { WarehouseArticleDialog } from './HistoryTab/WarehouseArticleDialog'
import { WarehouseHistoryTable } from './HistoryTab/WarehouseHistoryTable'
import { VehicleReportsTab } from './ReportsTab/VehicleReportsTab'
import { WarehouseStateTable } from './StateTab/WarehouseStateTable'
import { UploadsTab } from './UploadsTab/UploadsTab'

export default function WarehouseList() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') || 'state'
  const {
    state: stateList,
    history,
    isLoading,
    fetchState,
    fetchHistory,
    deleteArticle,
  } = useWarehouseStore()

  const [activeTab, setActiveTab] = useState<any>(initialTab)

  const handleTabChange = useCallback((key: any) => {
    setActiveTab(key)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (key === 'state') {
        next.delete('tab')
      } else {
        next.set('tab', key)
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  // Fetch data dynamically based on the active tab
  useEffect(() => {
    if (activeTab === 'state') {
      fetchState()
      fetchHistory()
    } else if (activeTab === 'history') {
      fetchHistory()
    }
  }, [activeTab, fetchState, fetchHistory])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [modalType, setModalType] = useState<'in' | 'out'>('in')
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>()
  const [deletingId, setDeletingId] = useState<string | undefined>()
  const [initialArticleName, setInitialArticleName] = useState<string | undefined>()

  const handleAdd = useCallback(() => {
    setEditingId(undefined)
    setInitialArticleName(undefined)
    setModalType('in')
    setIsModalOpen(true)
  }, [])

  const handleInstall = useCallback(() => {
    setEditingId(undefined)
    setInitialArticleName(undefined)
    setModalType('out')
    setIsModalOpen(true)
  }, [])

  const handleImport = useCallback(() => {
    setIsImportOpen(true)
  }, [])

  const handleInstallForArticle = useCallback((articleName: string) => {
    setEditingId(undefined)
    setInitialArticleName(articleName)
    setModalType('out')
    setIsModalOpen(true)
  }, [])

  const handleEdit = (id: string) => {
    const { vehicleReport } = useWarehouseStore.getState()
    const found = history.find(h => h.id === id) ?? vehicleReport.find(r => r.id === id)
    if (found) {
      setEditingId(id)
      setModalType(('isInstall' in found ? found.isInstall : true) ? 'out' : 'in')
      setIsModalOpen(true)
    }
  }

  const handleDeleteTrigger = (id: string) => {
    setDeletingId(id)
    setIsDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (deletingId) {
      await deleteArticle(deletingId)
      setIsDeleteOpen(false)
      setDeletingId(undefined)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingId(undefined)
    setInitialArticleName(undefined)
  }

  return (
    <div className="p-5 space-y-5">
      <PageHeader
        title={t('warehouse.title')}
        subtitle={t('warehouse.subtitle')}
        actions={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleImport}>
              <FileText />
              {t('warehouse.import_button')}
            </Button>
            <Button variant="tertiary" onClick={handleInstall}>
              <Wrench />
              {t('warehouse.new_install')}
            </Button>
            <Button onClick={handleAdd}>
              <Plus />
              {t('warehouse.add_article')}
            </Button>
          </div>
        }
      />

      {stateList.length === 0 && !isLoading ? (
        <EmptyState
          icon={<Box className={'size-8'} />}
          title={t('warehouse.empty_title')}
          description={t('warehouse.empty_subtitle')}
        >
          <div className="flex gap-4">
            <Button size="lg" onClick={handleAdd}>
              <Plus />
              {t('warehouse.empty_action')}
            </Button>
          </div>
        </EmptyState>
      ) : (
        <Tabs className="w-full" variant={'secondary'} selectedKey={activeTab} onSelectionChange={handleTabChange}>
          <Tabs.ListContainer>
            <Tabs.List aria-label="Warehouse content tabs" className="w-fit" >
              <Tabs.Tab id="state" >
                {t('warehouse.tab_state')}
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id="history" className={'w-40'}>
                {t('warehouse.tab_history')}
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id="reports">
                {t('warehouse.tab_reports')}
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id="uploads">
                {t('warehouse.tab_uploads', 'Логи загрузок')}
                <Tabs.Indicator />
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>

          {/* TAB 1: Warehouse State */}
          <Tabs.Panel className="pt-0" id="state">
            <WarehouseStateTable stateList={stateList} onInstall={handleInstallForArticle} />
          </Tabs.Panel>

          {/* TAB 2: History log with actions */}
          <Tabs.Panel className="pt-0" id="history">
            <WarehouseHistoryTable
              onEdit={handleEdit}
              onDelete={handleDeleteTrigger}
            />
          </Tabs.Panel>

          {/* TAB 3: Vehicle Reports */}
          <Tabs.Panel className="pt-0" id="reports">
            <VehicleReportsTab
              onDelete={handleDeleteTrigger}
            />
          </Tabs.Panel>

          {/* TAB 4: Upload Logs */}
          <Tabs.Panel className="pt-0" id="uploads">
            <UploadsTab />
          </Tabs.Panel>
        </Tabs>
      )}

      {modalType === 'in' ? (
        <WarehouseArticleDialog
          isOpen={isModalOpen}
          onOpenChange={open => !open && closeModal()}
          articleId={editingId}
        />
      ) : (
        <WarehouseInstallDialog
          isOpen={isModalOpen}
          onOpenChange={open => !open && closeModal()}
          articleId={editingId}
          initialArticleName={initialArticleName}
        />
      )}

      <WarehouseImportDialog
        isOpen={isImportOpen}
        onOpenChange={setIsImportOpen}
      />

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={t('warehouse.delete_confirm')}
        isLoading={isLoading}
      />
    </div>
  )
}
